// src/components/VitalSummaryCard.tsx

import type { Vitals, Posture, FaceFeatures } from '../types/triage';

interface VitalSummaryCardProps {
  vitals: Vitals;
  posture: Posture;
  respiratoryEffortScore: number;
  face: FaceFeatures;
}

export function VitalSummaryCard({
  vitals,
  posture,
  respiratoryEffortScore,
  face,
}: VitalSummaryCardProps) {
  return (
    <div className="w-full max-w-2xl mx-auto bg-white rounded-lg shadow-lg p-4 sm:p-6 space-y-3 sm:space-y-4">
      <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-3 sm:mb-4">
        Measurement Results
      </h2>

      {/* Vitals */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
        <div className="border rounded-lg p-3 sm:p-4">
          <div className="text-xs sm:text-sm text-gray-600">Heart Rate</div>
          <div className="text-xl sm:text-2xl font-bold text-gray-800">
            {vitals.hr_bpm !== null ? `${Math.round(vitals.hr_bpm)} bpm` : 'Not detected'}
          </div>
        </div>

        <div className="border rounded-lg p-3 sm:p-4">
          <div className="text-xs sm:text-sm text-gray-600">Respiratory Rate</div>
          <div className="text-xl sm:text-2xl font-bold text-gray-800">
            {vitals.rr_bpm !== null ? `${Math.round(vitals.rr_bpm)} /min` : 'Not detected'}
          </div>
        </div>
      </div>

      {/* Posture */}
      <div className="border rounded-lg p-3 sm:p-4">
        <div className="text-xs sm:text-sm text-gray-600">Posture</div>
        <div className="text-lg sm:text-xl font-bold text-gray-800 capitalize">
          {posture.label}
        </div>
      </div>

      {/* Respiratory Effort */}
      <div className="border rounded-lg p-3 sm:p-4">
        <div className="text-xs sm:text-sm text-gray-600">Respiratory Effort</div>
        <div className="text-lg sm:text-xl font-bold text-gray-800">
          Level {respiratoryEffortScore} / 3
        </div>
        <div className="text-xs text-gray-500">
          {respiratoryEffortScore === 0 && 'Normal'}
          {respiratoryEffortScore === 1 && 'Mild'}
          {respiratoryEffortScore === 2 && 'Moderate'}
          {respiratoryEffortScore === 3 && 'Severe'}
        </div>
      </div>

      {/* Face Features */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
        <div className="border rounded-lg p-3 sm:p-4">
          <div className="text-xs sm:text-sm text-gray-600">Eye Openness</div>
          <div className="text-lg sm:text-xl font-bold text-gray-800">
            {face.eye_open_ratio !== null && face.eye_open_ratio !== undefined
              ? `${(face.eye_open_ratio * 100).toFixed(0)}%`
              : 'Not detected'}
          </div>
        </div>

        <div className="border rounded-lg p-3 sm:p-4">
          <div className="text-xs sm:text-sm text-gray-600">Pain Expression</div>
          <div className="text-lg sm:text-xl font-bold text-gray-800">
            {face.pain_face_score !== null && face.pain_face_score !== undefined
              ? `${face.pain_face_score} / 3`
              : 'Not detected'}
          </div>
        </div>

        <div className="border rounded-lg p-3 sm:p-4">
          <div className="text-xs sm:text-sm text-gray-600">Pallor</div>
          <div className="text-lg sm:text-xl font-bold text-gray-800">
            {face.pallor_score !== null && face.pallor_score !== undefined
              ? `${(face.pallor_score * 100).toFixed(0)}%`
              : 'Not detected'}
          </div>
        </div>

        <div className="border rounded-lg p-3 sm:p-4">
          <div className="text-xs sm:text-sm text-gray-600">Diaphoresis</div>
          <div className="text-lg sm:text-xl font-bold text-gray-800">
            {face.sweat_flag !== null && face.sweat_flag !== undefined
              ? (face.sweat_flag ? 'Detected' : 'Not detected')
              : 'Unknown'}
          </div>
        </div>
      </div>
    </div>
  );
}
